import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from '@features/auth/contexts/AuthContext';
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { Input } from "@shared/components/ui/input";
import { useTranslation } from "react-i18next";
import { Package, Plus, Minus, Edit, History } from "lucide-react";
import { updateProductStock } from '@features/products/services/productService';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { StockMovementHistory } from "./StockMovementHistory";

export function StockEditDialog({ product, trigger }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  
  // Check if user can edit stock movements (admin or manager only)
  const canEditStock = user?.role === "admin" || user?.role === "manager";

  const stockUpdateMutation = useMutation({
    mutationFn: updateProductStock,
    onSuccess: (data) => {
      setOpen(false);
      toast.success(t("products.stockUpdatedSuccessfully"));
      form.reset({
        stock: data.product.stock,
        adjustment: 0,
      });
      // Invalidate and refetch product data
      queryClient.invalidateQueries(["products"]);
      queryClient.invalidateQueries(["product", product._id]);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || t("products.stockUpdateFailed")
      );
    },
  });

  const formSchema = z.object({
    stock: z.number().min(0, {
      message: t("products.validation.min", {
        field: t("products.form.stock"),
        min: 0,
      }),
    }),
    adjustment: z.number().int({
      message: t("products.validation.mustBeInteger"),
    }),
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      stock: product?.stock || 0,
      adjustment: 0,
    },
  });

  const watchedStock = form.watch("stock");
  const watchedAdjustment = form.watch("adjustment");

  const handleStockUpdate = (values) => {
    stockUpdateMutation.mutate({
      id: product._id,
      stock: values.stock,
      reason: "Manual stock adjustment via dialog",
      notes: `Stock adjusted from ${product?.stock || 0} to ${values.stock}`,
    });
  };

  const handleAdjustmentChange = (adjustment) => {
    const newStock = Math.max(0, (product?.stock || 0) + adjustment);
    form.setValue("stock", newStock);
    form.setValue("adjustment", adjustment);
  };

  const handleDirectStockChange = (stock) => {
    const adjustment = stock - (product?.stock || 0);
    form.setValue("adjustment", adjustment);
  };

  const resetForm = () => {
    form.reset({
      stock: product?.stock || 0,
      adjustment: 0,
    });
  };

  // If user doesn't have permission, don't render the component
  if (!canEditStock) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline" size="sm">
            <Edit className="w-4 h-4 mr-2" />
            {t("products.editStock")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {t("products.editStock")}
          </DialogTitle>
          <DialogDescription>
            {t("products.editStockDescription", { product: product?.name })}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleStockUpdate)}
            className="space-y-4"
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("products.currentStock")}
                </label>
                <div className="flex items-center justify-center p-3 bg-muted rounded-md">
                  <span className="text-lg font-semibold">
                    {product?.stock || 0}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("products.newStock")}
                </label>
                <div className="flex items-center justify-center p-3 bg-primary/10 rounded-md">
                  <span className="text-lg font-semibold text-primary">
                    {watchedStock}
                  </span>
                </div>
              </div>
            </div>

            <FormField
              control={form.control}
              name="stock"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("products.setStock")}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      {...field}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        field.onChange(value);
                        handleDirectStockChange(value);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <label className="text-sm font-medium">
                {t("products.quickAdjustments")}
              </label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAdjustmentChange(-10)}
                  disabled={watchedStock <= 0}
                >
                  <Minus className="w-4 h-4" />
                  10
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAdjustmentChange(-1)}
                  disabled={watchedStock <= 0}
                >
                  <Minus className="w-4 h-4" />1
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAdjustmentChange(1)}
                >
                  <Plus className="w-4 h-4" />1
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAdjustmentChange(10)}
                >
                  <Plus className="w-4 h-4" />
                  10
                </Button>
              </div>
            </div>

            {watchedAdjustment !== 0 && (
              <div className="text-sm text-muted-foreground">
                {watchedAdjustment > 0 ? (
                  <span className="text-green-600">
                    +{watchedAdjustment} {t("products.unitsAdded")}
                  </span>
                ) : (
                  <span className="text-red-600">
                    {watchedAdjustment} {t("products.unitsRemoved")}
                  </span>
                )}
              </div>
            )}

            <DialogFooter className="sm:justify-between">
              <StockMovementHistory
                product={product}
                trigger={
                  <Button type="button" variant="outline" size="sm">
                    <History className="w-4 h-4 mr-2" />
                    {t("products.viewHistory")}
                  </Button>
                }
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetForm();
                    setOpen(false);
                  }}
                  disabled={stockUpdateMutation.isPending}
                >
                  {t("common.close") || t("main.cancel")}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={resetForm}
                  disabled={stockUpdateMutation.isPending}
                >
                  {t("common.reset") || t("main.reset")}
                </Button>
                <Button
                  type="submit"
                  disabled={
                    stockUpdateMutation.isPending || watchedAdjustment === 0
                  }
                >
                  {t("products.updateStock")}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
